/**
 * Banner shown when the preview's column mapping was silently reused from a
 * remembered `availability_profile_mappings` row (keyed by column-header
 * signature) instead of freshly recognized from the current file
 * (CU-86cbafj4j). Kept deliberately plain: state that the mapping is
 * remembered, state when it was last saved if known, offer one action.
 * No mechanism explanation — that reads as filler once the label and action
 * already say what matters, and it buried the one fact operators actually
 * need (is this old, and since when).
 *
 * Uses `History` rather than `Sparkles` (the sibling `mapping-transforms-
 * auto-suggest-banner.tsx`'s icon) — `Sparkles` reads as "we cleverly
 * detected this," the opposite of what this banner communicates: the
 * mapping is NOT a fresh detection.
 *
 * Informational blue tone (not the auto-suggest-banner's neutral
 * `bg-muted/40`) — this must clearly mark the mapping as reused, not just
 * quietly note a convenience default.
 */
import { History } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';

export interface RememberedMappingBannerLabels {
  /** e.g. "Zapamiętane mapowanie" */
  heading: string;
  /** Template with a `{date}` placeholder, e.g. "z {date}" — used
   * whenever `lastImportedAtLabel` is provided. */
  dateTemplate: string;
  /** Suffix used when no date is known, e.g. "z poprzedniego importu". */
  noDateFallback: string;
  resetActionLabel: string;
}

export interface RememberedMappingBannerProps {
  labels: RememberedMappingBannerLabels;
  /** Pre-formatted date string (format via `src/lib/date-format.ts` at the
   * call site), e.g. "15.02.2026". Omit when the saved mapping has no known
   * date. */
  lastImportedAtLabel?: string;
  onReset: () => void;
  resetting?: boolean;
  testIdPrefix?: string;
}

export function RememberedMappingBanner({
  labels,
  lastImportedAtLabel,
  onReset,
  resetting = false,
  testIdPrefix = 'remembered-mapping',
}: RememberedMappingBannerProps) {
  const suffix = lastImportedAtLabel
    ? labels.dateTemplate.replace('{date}', lastImportedAtLabel)
    : labels.noDateFallback;

  return (
    <div
      className="rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 flex items-center gap-2 text-blue-800"
      data-testid={`${testIdPrefix}-banner`}
    >
      <History className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
      <span className="text-xs">
        {labels.heading} {suffix}
      </span>
      <Button
        type="button"
        variant="link"
        size="xs"
        className="ml-auto gap-1 text-blue-800 hover:text-blue-900"
        onClick={onReset}
        disabled={resetting}
        data-testid={`${testIdPrefix}-reset`}
      >
        {labels.resetActionLabel}
      </Button>
    </div>
  );
}
